/**
 * useAlertRouterData.ts — Hook for nself-alert-router plugin (port 3834)
 *
 * Purpose:    Fetch and manage alert routing policies, on-call schedule, and silences.
 * Inputs:     None (reads from plugin backend port 3834)
 * Outputs:    { state, data, error, refresh, savePolicy, deletePolicy, createSilence, deleteSilence }
 * Constraints: Client-side only; uses pluginFetch; AsyncState for UI
 * SOT:        T-P3-E5-W2-S4-T01
 */

import { useCallback, useEffect, useState } from 'react'
import { pluginFetch } from '../lib/plugin-client'
import type { SentryError } from '../lib/sentry-error'
import type { AsyncState } from '../components/AsyncScreen'

const PORT = 3834

export type AlertActionType = 'email' | 'slack' | 'webhook' | 'pagerduty'

export interface AlertCondition {
  field: 'severity' | 'source' | 'tag'
  operator: 'eq' | 'neq' | 'contains'
  value: string
}

export interface EscalationStep {
  delayMinutes: number
  actionType: AlertActionType
  target: string
}

export interface AlertPolicy {
  id: string
  name: string
  enabled: boolean
  conditions: AlertCondition[]
  escalationChain: EscalationStep[]
  createdAt: string
}

export interface OnCallSlot {
  day: 0 | 1 | 2 | 3 | 4 | 5 | 6
  startHour: number
  endHour: number
  assignee: string
}

export interface Silence {
  id: string
  matcher: string
  reason: string
  startsAt: string
  endsAt: string
  createdBy: string
}

export interface AlertRouterData {
  policies: AlertPolicy[]
  onCallSchedule: OnCallSlot[]
  silences: Silence[]
}

export function useAlertRouterData() {
  const [state, setState] = useState<AsyncState>('loading')
  const [data, setData] = useState<AlertRouterData | null>(null)
  const [error, setError] = useState<SentryError | undefined>()

  const load = useCallback(async () => {
    setState('loading')
    setError(undefined)
    if (!navigator.onLine) { setState('offline'); return }
    const result = await pluginFetch<AlertRouterData>(PORT, '/api/alert-router/summary')
    if (result.ok) {
      setData(result.value)
      setState('success')
    } else {
      const e = result.error
      if (e.type === 'bundle_required') setState('permission-denied')
      else if (e.type === 'rate_limit') setState('rate-limited')
      else if (e.type === 'plugin_offline') setState('offline')
      else setState('error')
      setError(e)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const savePolicy = useCallback(
    async (policy: Omit<AlertPolicy, 'id' | 'createdAt'> & { id?: string }): Promise<boolean> => {
      const isNew = !policy.id
      const result = await pluginFetch(
        PORT,
        isNew ? '/api/alert-router/policies' : `/api/alert-router/policies/${policy.id}`,
        { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(policy) },
      )
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const deletePolicy = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/alert-router/policies/${id}`, { method: 'DELETE' })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const createSilence = useCallback(
    async (silence: Omit<Silence, 'id' | 'createdBy'>): Promise<boolean> => {
      const result = await pluginFetch(PORT, '/api/alert-router/silences', {
        method: 'POST',
        body: JSON.stringify(silence),
      })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  const deleteSilence = useCallback(
    async (id: string): Promise<boolean> => {
      const result = await pluginFetch(PORT, `/api/alert-router/silences/${id}`, { method: 'DELETE' })
      if (result.ok) { await load() }
      return result.ok
    },
    [load],
  )

  return { state, data, error, refresh: load, savePolicy, deletePolicy, createSilence, deleteSilence }
}
